/**
 * Populated view when an API sequence exists for the workflow.
 *
 * @module FlowDesigner/api/ApiSequenceView
 */

import React from 'react';
import { Warning } from '@phosphor-icons/react';
import { Text } from '@chatsdet/ui';
import { motion, AnimatePresence } from 'framer-motion';
import type { ApiSequenceConfig } from '@chatsdet/types';
import type { SeedDataOutput } from '../../../../hooks/useSeedWorkflowOutputs';
import {
  CollapsibleStepRow,
  StepDetailSection,
} from '../../../components/node-config/StepCard';
import { SeedOutputMappingSection } from './SeedOutputMappingSection';
import { SeedDependencySection, type SeedDependencyInfo } from './SeedDependencyCard';

type ApiSequenceViewProps = {
  sequence: {
    id: string;
    workflowId: string;
    config: ApiSequenceConfig;
    status: 'draft' | 'ready' | 'stale';
  };
  seedOutputs?: SeedDataOutput[];
  seedDependencies?: SeedDependencyInfo[];
};

export const ApiSequenceView: React.FC<ApiSequenceViewProps> = ({
  sequence,
  seedOutputs,
  seedDependencies,
}) => {
  const [expandedStep, setExpandedStep] = React.useState<number | null>(null);
  const steps = sequence.config.steps ?? [];
  const isStale = sequence.status === 'stale';

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-w-0 min-h-0 bg-muted/30">
      {/* Steps list */}
      <div className="flex-1 overflow-y-auto p-4 pb-16 space-y-3">
        {isStale && (
          <div className="flex items-center gap-1.5 px-1 pb-2">
            <Warning size={14} className="text-warning flex-shrink-0" weight="fill" />
            <Text size="sm" className="text-warning">
              Workflow has changed since this was generated.
            </Text>
          </div>
        )}
        {seedDependencies && seedDependencies.length > 0 && (
          <SeedDependencySection dependencies={seedDependencies} />
        )}
        <div className="border border-border rounded-lg divide-y divide-border/50 overflow-hidden bg-card">
          {steps.map((step, i) => {
            const isExpanded = expandedStep === i;

            return (
              <div key={step.stepId ?? i}>
                <CollapsibleStepRow
                  step={step}
                  index={i}
                  isExpanded={isExpanded}
                  onToggle={() => setExpandedStep(isExpanded ? null : i)}
                />
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <StepDetailSection step={step} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {seedOutputs && seedOutputs.length > 0 && (
          <SeedOutputMappingSection
            seedOutputs={seedOutputs}
            exports={sequence.config.exports ?? []}
            steps={steps}
            sequenceId={sequence.id}
            workflowId={sequence.workflowId}
          />
        )}
      </div>
    </div>
  );
};
